import { useEffect, useId, useRef, type FormEvent, type ReactNode } from 'react'
import { Button, type ButtonVariant } from './Button'
import { ErrorBox } from './Feedback'
import s from './Dialog.module.scss'

interface Props {
  titel: string
  /** Beschriftung des bestätigenden Knopfes — sagt, was passiert. */
  bestaetigen: string
  variant?: ButtonVariant
  /** Gesperrt, solange die Eingabe unvollständig ist. */
  gesperrt?: boolean
  /** Der Vorgang läuft: beide Knöpfe zu, kein Abbruch per Escape. */
  laeuft?: boolean
  /** Fehlschlag des letzten Versuchs. Bleibt stehen, der Dialog auch. */
  fehler?: string | null
  onBestaetigen: () => void
  onAbbrechen: () => void
  children?: ReactNode
}

/**
 * Ein Dialog, der etwas erfragt und bestätigen lässt.
 *
 * Der Aufrufer rendert ihn bedingt — `{offen && <Dialog …>}` — statt ihm ein
 * `offen` zu übergeben. Dann verwirft React beim Schliessen den Zustand der
 * Felder darin, und der nächste Aufruf beginnt leer, ohne dass jemand
 * zurücksetzen müsste.
 */
export function Dialog({
  titel, bestaetigen, variant = 'primary', gesperrt, laeuft, fehler,
  onBestaetigen, onAbbrechen, children,
}: Props) {
  const ref = useRef<HTMLDialogElement>(null)
  const titelId = useId()

  // showModal() und nicht das open-Attribut: Nur der modale Aufruf bringt
  // Fokusfalle, Escape-Taste und ::backdrop mit, und nur er legt den Dialog
  // in die Top-Layer — über jedes overflow und jeden z-index der Seite. Ein
  // <dialog open> läge im normalen Fluss und stünde hinter der Kopfzeile.
  useEffect(() => { ref.current?.showModal() }, [])

  function absenden(event: FormEvent) {
    event.preventDefault()
    if (!gesperrt && !laeuft) onBestaetigen()
  }

  return (
    <dialog
      ref={ref}
      className={s.dialog}
      aria-labelledby={titelId}
      // Escape löst `cancel` aus. Während der Vorgang läuft, bleibt der
      // Dialog stehen: Sonst verschwände er, während im Hintergrund noch
      // geschrieben wird, und niemand sähe den Ausgang.
      onCancel={(event) => {
        event.preventDefault()
        if (!laeuft) onAbbrechen()
      }}
      // Ein Klick daneben trifft das <dialog> selbst — die Kinder liegen im
      // Kasten darin. So schliesst der Klick auf den Hintergrund, ohne dass
      // ein Klick auf ein Feld es täte.
      onClick={(event) => { if (event.target === ref.current && !laeuft) onAbbrechen() }}
    >
      <form className={s.kasten} onSubmit={absenden}>
        <h2 className={s.titel} id={titelId}>{titel}</h2>

        {children && <div className={s.inhalt}>{children}</div>}

        {fehler && <ErrorBox>{fehler}</ErrorBox>}

        <footer className={s.fuss}>
          <Button variant="plain" onClick={onAbbrechen} disabled={laeuft}>
            Abbrechen
          </Button>
          <Button variant={variant} type="submit" disabled={gesperrt || laeuft}>
            {laeuft ? 'läuft …' : bestaetigen}
          </Button>
        </footer>
      </form>
    </dialog>
  )
}
